import React, { useMemo } from 'react'

import {
  Button,
  Checkbox,
  CheckboxGroup,
  CheckboxLabel,
  DataTable,
  DataTableColumn,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  Typography
} from '@barracuda-internal/bds-core'
import { Block } from '@barracuda-internal/bds-core/dist/Icons/Core'
import { Warning } from '@barracuda-internal/bds-core/dist/Icons/Feedback'
import { EnhancedEncryption } from '@material-ui/icons'

import outboundSettingsStyles from 'components/pages/outboundSettings/outboundSettingsStyles'
import { useFormatMessage } from 'lib/localization'
import {
  AVAILABLE_PREDEFINED_FILTER_PATTERN,
  PredefinedFilterMatches,
  OutboundPredefinedFilterAction,
  PredefinedFilter
} from 'types/Settings'
import { Cell } from 'components/libs/grid/cell'
import WarningDialog from 'components/libs/dialog/warningDialog/WarningDialog'
import {
  usePredefinedFiltersTableLogic,
  SELECT_NAMES,
  UsePredefinedFiltersTableLogicProps
} from 'components/pages/outboundSettings/contentPolicies/usePredefinedFiltersTableLogic'
import { useIsReadonly } from 'lib/hooks/useIsReadonly'

const BASE_I18N_KEY = 'ess.outbound_settings.content_policies'
const BASE_INBOUND_I18N_KEY = 'ess.inbound_settings'
const DIALOG_I18N_KEY = 'ess.dialog'

const PredefinedFiltersTable = (props: UsePredefinedFiltersTableLogicProps) => {
  const isReadonly = useIsReadonly()
  const outboundClasses = outboundSettingsStyles()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const formatDialogMessage = useFormatMessage(DIALOG_I18N_KEY)
  const formatInboundMessage = useFormatMessage(BASE_INBOUND_I18N_KEY)

  const [state, eventHandlers] = usePredefinedFiltersTableLogic(props)

  return useMemo(
    () => (
      <Grid container>
        <Grid container>
          <Typography variant="h4">{formatMessage('predefined_filters')}</Typography>
          <Button
            className={outboundClasses.bulkEdit}
            onClick={eventHandlers.onBulkEdit}
            variant="contained"
            size="small"
            data-testid="bulk-button"
            disabled={isReadonly}
          >
            {formatInboundMessage('bulk_edit')}
          </Button>
        </Grid>
        <Grid container>
          <Typography variant="body2">{formatMessage('predefined_filters_description')}</Typography>
        </Grid>
        <DataTable
          sortable={{
            allowUnsort: false,
            mode: 'single'
          }}
          sort={state.sort}
          onSortChange={eventHandlers.handleSortChange}
          data={state.tableData}
        >
          <DataTableColumn
            sortable
            field="pattern"
            title={formatMessage('pattern')}
            width={300}
            cell={({ dataItem }: { dataItem: PredefinedFilter }) => (
              <Cell>
                {dataItem.id === '' && (
                  <>
                    <Select
                      variant="outlined"
                      fullWidth
                      name={SELECT_NAMES.PATTERN}
                      defaultValue={state.formState.pattern}
                      onChange={eventHandlers.handleOnSelectChange}
                      SelectDisplayProps={{ style: { padding: 8 } }}
                      data-testid="new-pattern-field"
                      error={!!state.errors.pattern}
                      disabled={isReadonly}
                    >
                      {AVAILABLE_PREDEFINED_FILTER_PATTERN.map(type => (
                        <MenuItem key={type} value={type}>
                          {formatMessage(type)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText className={outboundClasses.errorColor}>
                      {state.errors.pattern && formatMessage(state.errors.pattern)}
                    </FormHelperText>
                  </>
                )}
                {dataItem.id !== '' && (
                  <Typography variant="body2" data-testid="pattern-field" className={outboundClasses.flexWrapper}>
                    {formatMessage(dataItem.pattern)}
                  </Typography>
                )}
              </Cell>
            )}
          />
          <DataTableColumn
            sortable
            field="action"
            title={formatMessage('action')}
            width={200}
            cell={({ dataItem }: { dataItem: PredefinedFilter }) => (
              <Cell>
                {dataItem.id === '' && (
                  <Select
                    variant="outlined"
                    className={outboundClasses.smallDropdown}
                    name={SELECT_NAMES.ACTION}
                    defaultValue={state.formState.action}
                    onChange={eventHandlers.handleOnSelectChange}
                    SelectDisplayProps={{ style: { padding: 8 } }}
                    data-testid="new-action-field"
                    disabled={isReadonly}
                  >
                    {state.actionOptions.map(type => (
                      <MenuItem key={type} value={type}>
                        {formatMessage(type)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                {dataItem.id !== '' && (
                  <Typography variant="body2" data-testid="action-field" className={outboundClasses.flexWrapper}>
                    {dataItem.action === OutboundPredefinedFilterAction.BLOCK && (
                      <Block className={outboundClasses.block} />
                    )}
                    {dataItem.action === OutboundPredefinedFilterAction.QUARANTINE && (
                      <Warning className={outboundClasses.quarantine} />
                    )}
                    {dataItem.action === OutboundPredefinedFilterAction.ENCRYPT && (
                      <EnhancedEncryption className={outboundClasses.ignoreColor} />
                    )}
                    {formatMessage(dataItem.action)}
                  </Typography>
                )}
              </Cell>
            )}
          />
          <DataTableColumn
            sortable
            field="match"
            title={formatMessage('match')}
            cell={({ dataItem }: { dataItem: PredefinedFilter }) => (
              <Cell className={outboundClasses.smallCell}>
                {!dataItem.id && (
                  <Grid>
                    <CheckboxGroup row>
                      {Object.values(PredefinedFilterMatches).map(value => (
                        <CheckboxLabel
                          control={
                            <Checkbox
                              size="small"
                              name={value}
                              checked={!!state.formState.match[value]}
                              onChange={eventHandlers.handleOnCheckboxChange}
                              disabled={isReadonly}
                            />
                          }
                          label={formatMessage(value)}
                          key={value}
                          data-testid={`checkbox-${value}`}
                        />
                      ))}
                    </CheckboxGroup>
                    <FormHelperText className={outboundClasses.errorColor}>
                      {state.errors.match && formatMessage(state.errors.match)}
                    </FormHelperText>
                  </Grid>
                )}
                {dataItem.id && (
                  <Typography variant="body2" data-testid="match-field">
                    {Object.keys(dataItem.match)
                      .map(key => ` ${formatMessage(key)}`)
                      .toString()}
                  </Typography>
                )}
              </Cell>
            )}
          />
          <DataTableColumn
            field="actions"
            title={formatMessage('actions')}
            width={200}
            cell={({ dataItem }: { dataItem: PredefinedFilter }) => (
              <Cell data-testid="action-cell" className={outboundClasses.smallCell}>
                {!dataItem.id && (
                  <Button
                    color="primary"
                    onClick={() => {
                      eventHandlers.onAdd()
                    }}
                    variant="text"
                    data-testid="add-button"
                    disabled={isReadonly}
                  >
                    {formatMessage('add')}
                  </Button>
                )}
                {dataItem.id && (
                  <Button
                    color="primary"
                    onClick={() => {
                      eventHandlers.onRemove(dataItem.id)
                    }}
                    variant="text"
                    data-testid="remove-button"
                    disabled={isReadonly}
                  >
                    {formatMessage('remove')}
                  </Button>
                )}
              </Cell>
            )}
          />
        </DataTable>
        {state.isConfirmDialogOpen && (
          <WarningDialog
            title={formatDialogMessage('confirm_removal')}
            onCancel={eventHandlers.onRemoveCancel}
            onConfirm={eventHandlers.onRemoveConfirm}
          />
        )}
      </Grid>
    ),
    [
      formatMessage,
      outboundClasses.bulkEdit,
      outboundClasses.errorColor,
      outboundClasses.flexWrapper,
      outboundClasses.smallDropdown,
      outboundClasses.block,
      outboundClasses.quarantine,
      outboundClasses.ignoreColor,
      outboundClasses.smallCell,
      eventHandlers,
      isReadonly,
      formatInboundMessage,
      state.sort,
      state.tableData,
      state.isConfirmDialogOpen,
      state.formState.pattern,
      state.formState.action,
      state.formState.match,
      state.errors.pattern,
      state.errors.match,
      state.actionOptions,
      formatDialogMessage
    ]
  )
}

export default PredefinedFiltersTable
